/**
 * 챌린지 데이터 접근 — QBase 등록 API 를 직접 호출한다.
 *
 * 예전 경로: 브라우저 → Node(:3003) /api/challenge/* → coreServerClient(서비스계정) → QBase
 * 지금 경로: 브라우저 → QBase (본인 세션)
 *
 * ★ 응답 봉투는 예전 프록시가 주던 모양을 그대로 유지한다.
 *   challengeActions.js 가 이미 camelCase → UPPER_SNAKE 매핑을 하고 있고, 그 매핑이
 *   기대하는 키는 QBase 뷰의 camelCase 와 정확히 일치한다. 봉투만 맞추면 화면 코드는
 *   한 줄도 바뀌지 않는다 — 이번 변경의 위험을 인증 경로 하나로 좁히기 위함이다.
 *
 * 등록 SQL: qbase/backend/scratchpad/LIVESQL_register.sql
 */

import axios from 'axios';
import { qbaseSelect, qbasePost, qbaseGet } from './qbaseClient';
import i18n from '../i18n';
import { LIVESQL_API, LIVESQL_LEGACY_API, QUERY_PARAM } from '../config/qbase';
import { isLocal, LOCAL_API } from './challengeSource';

/**
 * ## 공급원이 둘이다 (2026-09-19)
 *
 * 내부망에는 QBase 가 없어 챌린지가 통째로 사라졌다. 파일로 가져온 문제를 LiveSQL 백엔드가
 * 주면 화면은 그대로 돈다 — **로컬 API 의 응답 봉투를 QBase 와 같게** 맞춰 뒀기 때문이다.
 * 갈림길을 여기 다섯 함수에만 두어, 화면 코드는 어느 쪽에서 왔는지 몰라도 되게 한다.
 */
const local = (path, params) => axios.get(`${LOCAL_API}${path}`, { params }).then(r => r.data);

/** 챌린지 목록. 난이도 미지정 시 서버가 해당 절을 제거해 전체를 돌려준다. */
export async function listChallenges(challengeLevelDcd) {
  if (await isLocal()) {
    const data = await local('/challenges', challengeLevelDcd ? { level: challengeLevelDcd } : undefined);
    return data.qryChallengeLst || [];
  }
  return qbaseSelect(LIVESQL_API.challenges,
    challengeLevelDcd ? { [QUERY_PARAM.challengeLevel]: challengeLevelDcd } : undefined);
}

/** 챌린지의 퀴즈 목록 */
export async function listQuiz(challengeId) {
  if (await isLocal()) return local(`/challenges/${encodeURIComponent(challengeId)}/quizzes`);
  const rows = await qbaseSelect(LIVESQL_API.challengeQuiz, { [QUERY_PARAM.challengeId]: challengeId });
  return { qryChallengeQuizLst: rows };
}

/**
 * 챌린지의 참여 기록.
 *
 * 예전에는 usrid 를 파라미터로 넘겼지만 서버는 그것으로 거르지 않았다(챌린지 단위 전체 반환).
 * 화면도 전체를 받아 표에 뿌리므로 동작은 같다. 인자를 남겨 둔 것은 호출부 호환 때문이며,
 * 필터가 필요해지면 등록 API 의 whereconds 에 조건을 더하는 쪽이 맞다(서버가 거르게).
 */
export async function listParticipation(challengeId /* , userId */) {
  if (await isLocal()) return local(`/challenges/${encodeURIComponent(challengeId)}/participation`);
  const rows = await qbaseSelect(LIVESQL_API.challengeQuizPtcp, { [QUERY_PARAM.challengeId]: challengeId });
  return { qryChallengeQuizPtcpLst: rows };
}

/**
 * 챌린지 머리 칸 — camelCase → QBase 대문자 칸.
 * **공개 여부(OPEN_YN·OPEN_SCOPE_CD)는 싣지 않는다**(QBase 계약 변경 2026-10-07 — 실으면 400). 새 챌린지는 비공개로 만들어지고,
 * 공개 범위는 대상(그룹·사용자)과 함께 QBase `setChallengeScope` 한 곳에서만 바꾼다(그 계약은 QBase 세션이 보내 주기로 함 — 받는 대로 연결).
 */
const QBASE_CHALLENGE_COLS = {
  challengeNm: 'CHALLENGE_NM', bgngDt: 'BGNG_DT', endDt: 'END_DT',
  challengeLevelDcd: 'CHALLENGE_LEVEL_DCD', challengeAttrCn: 'CHALLENGE_ATTR_CN',
};

/** 문항 칸 — 교환 형식(camelCase) → QBase(대문자). 통째 교체라 **모든 칸을 문자열로** 보낸다(빠진 칸·null 은 '' 가 된다) */
const QBASE_QUIZ_COLS = {
  quizNo: 'QUIZ_NO', quizTcd: 'QUIZ_TCD', quizDc: 'QUIZ_DC', quizHint: 'QUIZ_HINT',
  cransSqlCn: 'CRANS_SQL_CN', cransRstCn: 'CRANS_RST_CN', addCransVerifCn: 'ADD_CRANS_VERIF_CN', atchFileLstCn: 'ATCH_FILE_LST_CN',
};
/** QBase 칸 길이(varchar 4000) — 넘으면 DB 오류(500)라 화면이 먼저 막는다(utils/challengeEdit.qbaseTooLong) */
export const QBASE_TEXT_LIMIT = 4000;
export const qbaseQuizRow = (q) => Object.fromEntries(Object.entries(QBASE_QUIZ_COLS).map(([k, col]) => {
  const v = q[k];
  return [col, v == null ? '' : (typeof v === 'object' ? JSON.stringify(v) : String(v))];
}));

/** 챌린지 머리 칸(camelCase) → QBase 칸. 보낸 칸만 바뀐다(기존 챌린지). MGR_USRID 는 서버가 세션으로 정한다 */
export const qbaseHeaderRow = (h) => {
  const row = {};
  for (const [k, col] of Object.entries(QBASE_CHALLENGE_COLS)) if (h && h[k] != null) row[col] = String(h[k]);
  return row;
};

/**
 * 클라우드에 챌린지를 **제출**한다(submitChallenge, 2026-10-06). 머리 + 문항을 한 번에 — 내용이 바뀌면 QBase 가 공용 LLM 으로 판정하고
 * 통과한 것만 저장한다. 판정 결과를 그대로 돌려준다(던지지 않는다): pass · notRequired 는 저장됨, fix · reject 는 아무것도 안 씀.
 * 403(남의 챌린지)·400(형식)·429(LLM 한도)·502(모델 오류)·500 은 QbaseError 로 던진다(status 로 가른다).
 * @param p { id?: 기존 번호, header: camelCase(바꿀 칸만), quizzes: 교환 형식 문항(번호 그대로), acceptFix?: fixToken }
 */
export async function submitQbaseChallenge({ id, header, quizzes, acceptFix } = {}) {
  const body = { header: qbaseHeaderRow(header || {}), rows: (quizzes || []).map(qbaseQuizRow) };
  if (id) body.QRY_CHALLENGE_ID = String(id);
  if (acceptFix) body.acceptFix = acceptFix;
  try {
    return await qbasePost(LIVESQL_API.submitChallenge, body);
  } catch (e) {
    // fix · reject 는 isSuccess:'N' 이라 파서가 던진다 — 판정 결과이지 오류가 아니므로 본문을 돌려준다
    if (e && e.body && (e.body.verdict === 'fix' || e.body.verdict === 'reject')) return e.body;
    throw e;
  }
}

// ── 공개 범위 · 참여그룹 · 좋아요 · 목록 집계(QBase 2026-10-07) ─────────────────
//   모두 **클라우드 전용**이다 — 「가져온 것」(로컬)에는 공유할 사람이 없다(이 PC 의 사용자 한 명). 로컬에서는 집계를 빈 값으로 채운다.

/** 공개 범위 코드 — QBase OPEN_SCOPE_CD 와 같은 값 */
export const OPEN_SCOPES = ['ALL', 'GROUP', 'USER', 'PRIVATE'];

/**
 * 선택 팝업의 목록 — 보이는 챌린지(서버가 거름) + 집계(등록일·작성자·참여자·좋아요·내 기록). 등록 최신순.
 * QBase 는 null 칸을 **응답에서 빼므로**(MyBatis 기본) 여기서 기본값을 채운다 — 화면이 칸마다 없음을 따지지 않게.
 */
export async function listChallengeSummary() {
  if (await isLocal()) {
    const data = await local('/challenges');
    return (data.qryChallengeLst || []).map(c => normalizeSummary({
      qryChallengeId: c.qryChallengeId ?? c.challengeId, challengeNm: c.qryChallengeNm ?? c.challengeNm,
      challengeLevelDcd: c.challengeLevelDcd, challengeAttrCn: c.challengeAttrCn, quizCnt: c.quizCnt,
      regDt: c.regDt || c.frstRegDt, openScopeCd: 'PRIVATE', mgrUsrid: c.mgrUsrid,
    }));
  }
  return (await qbaseSelect(LIVESQL_API.challengeSummary)).map(normalizeSummary);
}

/** 집계 행 → 화면 모양(빠진 칸 = 값 없음) */
export function normalizeSummary(r) {
  const n = (v) => (v == null || v === '' ? 0 : Number(v) || 0);
  return {
    ...r,
    qryChallengeId: String(r.qryChallengeId ?? ''),
    challengeNm: r.challengeNm || '',
    openScopeCd: OPEN_SCOPES.includes(r.openScopeCd) ? r.openScopeCd : (r.openYn === 'Y' ? 'ALL' : 'PRIVATE'),
    regDt: r.regDt ? String(r.regDt) : '',
    mgrUserKnme: r.mgrUserKnme || '',
    quizCnt: n(r.quizCnt),
    ptcpUserCnt: n(r.ptcpUserCnt),
    likeCnt: n(r.likeCnt),
    myLikeYn: r.myLikeYn === 'Y' ? 'Y' : 'N',
    myQuizCnt: n(r.myQuizCnt),
    myCorrectCnt: n(r.myCorrectCnt),
    myLastSbmsnDt: r.myLastSbmsnDt ? String(r.myLastSbmsnDt) : '',
  };
}

/** 좋아요 토글 → { myLikeYn, likeCnt }. 자기 챌린지·볼 수 없는 챌린지는 400(QbaseError) */
export async function toggleChallengeLike(challengeId) {
  const r = await qbasePost(LIVESQL_API.toggleChallengeLike, { QRY_CHALLENGE_ID: String(challengeId) });
  return { myLikeYn: r.myLikeYn === 'Y' ? 'Y' : 'N', likeCnt: Number(r.likeCnt) || 0 };
}

/**
 * 공개 범위 — 대상(그룹·사용자) 목록은 **통째로 교체**된다. GROUP 은 groupIds, USER 는 usrids 가 하나 이상(200개까지).
 * 작성자·LSADMIN 만(아니면 403). 새 챌린지는 submit 으로 받은 번호로 이어서 부른다. LLM 판정은 거치지 않는다.
 */
export async function setChallengeScope({ challengeId, scope, groupIds = [], usrids = [] }) {
  const body = { QRY_CHALLENGE_ID: String(challengeId), OPEN_SCOPE_CD: scope };
  if (scope === 'GROUP') body.groupIds = groupIds.map(String);
  if (scope === 'USER') body.usrids = usrids.map(String);
  return qbasePost(LIVESQL_API.setChallengeScope, body);
}

/** 참여그룹 가입 — 초대코드는 **서버가** 확인한다(틀리면 400). 다시 가입하면 예전 행이 되살아난다 */
export async function joinChallengeGroup(groupId, inviteCode) {
  const r = await qbasePost(LIVESQL_API.joinChallengeGroup, { QRY_CHALLENGE_PTCP_GRP_ID: String(groupId).trim(), INVITE_CD: String(inviteCode).trim() });
  return { qryChallengePtcpGrpId: r.qryChallengePtcpGrpId, ptcpGrpNm: r.ptcpGrpNm };
}

/** 참여그룹 탈퇴 — 가입하지 않은 그룹이면 400 */
export async function leaveChallengeGroup(groupId) {
  return qbasePost(LIVESQL_API.leaveChallengeGroup, { QRY_CHALLENGE_PTCP_GRP_ID: String(groupId) });
}

/** 공개 범위와 지금 대상 — { code, groups:[{id,name}], users:[{usrid,name}] }. 작성자·LSADMIN 만(아니면 403) */
export async function getChallengeScope(challengeId) {
  const r = await qbaseGet(LIVESQL_API.challengeScope, { QRY_CHALLENGE_ID: String(challengeId) });
  return {
    code: OPEN_SCOPES.includes(r.openScopeCd) ? r.openScopeCd : 'PRIVATE',
    groups: (r.groups || []).map(g => ({ id: g.qryChallengePtcpGrpId, name: g.ptcpGrpNm || g.qryChallengePtcpGrpId })),
    users: (r.users || []).map(u => ({ usrid: u.usrid, name: u.userKnme || '' })),
  };
}

/** 가입할 그룹 고르기 — 사용 중인 그룹(이름순). **초대코드는 없다**(코드는 가입할 때 서버가 확인) */
export async function listChallengeGroups() {
  if (await isLocal()) return [];
  return (await qbaseSelect(LIVESQL_API.challengeGroups)).map(g => ({ id: g.qryChallengePtcpGrpId, name: g.ptcpGrpNm || g.qryChallengePtcpGrpId }));
}

/** 내가 가입한 참여그룹(사용 중인 것만). 로컬에는 그룹이 없다 */
export async function listMyChallengeGroups() {
  if (await isLocal()) return [];
  return (await qbaseSelect(LIVESQL_API.myChallengeGroups)).filter(g => g.useYn !== 'N');
}

/** 판정 기준(시스템 프롬프트 전문) — 본인 LLM 「AI 검토」 에 그대로 싣는다(서버 판정과 기준·출력 형식이 같아진다) */
export async function fetchReviewCriteria() {
  return qbaseGet(LIVESQL_API.challengeReviewCriteria);
}

/** 「가져온 것」에 이미 있는 챌린지 번호 — 새 챌린지 번호가 겹치면 가져오기가 그것을 **덮어쓴다** */
export async function listLocalChallengeIds() {
  const data = await local('/challenges');
  return (data.qryChallengeLst || []).map(c => String(c.qryChallengeId ?? c.challengeId));
}

/**
 * 참여그룹에 초대된 챌린지 — challengeActions 가 { success, invitedChallenges } 를 읽는다.
 *
 * 호출부가 groupName 을 우선해 넘기지만 예전 프록시도 그 값을 그대로 그룹 **ID** 자리에
 * 넣어 보냈다. 의미를 바꾸지 않기 위해 같은 자리에 그대로 넘긴다.
 */
export async function listInvitedChallenges(groupIdentifier) {
  if (await isLocal()) return { success: true, invitedChallenges: [] };
  const rows = await qbaseSelect(LIVESQL_API.invitedChallenges, { [QUERY_PARAM.groupId]: groupIdentifier });
  return { success: true, invitedChallenges: rows };
}

/**
 * 퀴즈 참여 저장.
 *
 * ★ usrid 는 서버가 **세션에서** 정한다. 본문에 실려 가더라도 무시된다
 *   (LivesqlController.saveQryChallengeQuizPtcp). 예전에는 본문 값을 그대로 믿었고,
 *   서버가 보는 호출자가 서비스계정 하나뿐이라 그럴 수밖에 없었다.
 */
export async function saveParticipation(participationData) {
  if (await isLocal()) {
    // 「삭제」(초기화)는 저장이 아니다 — 예전엔 이 분기가 저장구분을 보지 않아 **빈 제출이 하나 더 쌓였다**(2026-10-05).
    // 판별은 challengeService.deleteParticipation 이 싣는 영문 표식(op)으로 한다
    if (participationData.op === 'delete') {
      return axios.delete(`${LOCAL_API}/challenges/${encodeURIComponent(participationData.challengeId)}/participation/${encodeURIComponent(participationData.quizId)}`)
        .then(r => r.data);
    }
    // 로컬도 **usrid 를 본문으로 믿지 않는다** — 서버가 호출자로 정한다(QBase 와 같은 규칙).
    return axios.post(`${LOCAL_API}/challenges/participation`, {
      challengeId: participationData.challengeId,
      quizNo: participationData.quizId,
      sbmsnSqlCn: participationData.ptcpSql,
      succssYn: participationData.successYn,
      failRsnCn: participationData.failRsn || null,
    }).then(r => r.data);
  }
  // op 는 로컬 분기용 표식이다 — QBase 계약에는 없으니 빼고 보낸다
  const { op: _op, ...qbaseData } = participationData;
  const res = await qbasePost(LIVESQL_LEGACY_API.saveQuizParticipation, qbaseData);
  // ★ 이 엔드포인트는 실패해도 **HTTP 200** 을 준다 — 예외를 안에서 잡고
  //   { status: 'error', message } 로 돌려주기 때문이다. 표준 응답(isSuccess/success)이
  //   아니라서 qbaseClient 의 공통 판정에 걸리지 않는다. 여기서 읽어 주지 않으면
  //   저장 실패가 화면에 성공으로 보인다.
  if (res && res.status === 'error') {
    throw new Error(res.message || i18n.t('challenge:repo.saveFailed'));
  }
  return res;
}

/** 퀴즈 첨부 다운로드 URL (파일 I/O 는 아직 Java 실코드가 담당한다) */
export function quizFileUrl(challengeId, quizNo, filename) {
  const q = new URLSearchParams({ challengeId, quizNo, filename }).toString();
  return `${LIVESQL_LEGACY_API.downloadQuizFile}?${q}`;
}
